import React from 'react';
import { 
  RefreshCw, 
  Calendar, 
  ShoppingCart, 
  MessageCircle,
  Download,
  Star
} from 'lucide-react';

const PaymentActions = ({ 
  payment, 
  order, 
  onRetryPayment, 
  onScheduleService, 
  onReorder 
}) => {
  if (!payment) return null;

  const getAvailableActions = () => {
    const actions = [];

    // Retry payment for failed payments
    if (payment?.status === 'failed' || payment?.status === 'cancelled') {
      actions?.push({
        id: 'retry',
        label: 'Retry Payment',
        icon: RefreshCw,
        onClick: onRetryPayment,
        variant: 'primary',
        description: 'Try your payment again with the same or different method'
      });
    }

    // Schedule service for completed payments with services
    if (payment?.status === 'completed' && hasServices()) {
      actions?.push({
        id: 'schedule',
        label: 'Schedule Service',
        icon: Calendar,
        onClick: onScheduleService,
        variant: 'secondary',
        description: 'Book your cleaning service appointment'
      });
    }

    // Reorder for completed payments
    if (payment?.status === 'completed') {
      actions?.push({
        id: 'reorder',
        label: 'Order Again',
        icon: ShoppingCart,
        onClick: onReorder,
        variant: 'secondary',
        description: 'Place another order with the same items'
      });
    }

    // Leave review for completed services
    if (payment?.status === 'completed' && hasServices() && isServiceCompleted()) {
      actions?.push({
        id: 'review',
        label: 'Leave Review',
        icon: Star,
        onClick: () => {/* Handle review */},
        variant: 'secondary',
        description: 'Share your experience with our service'
      });
    }

    // Download receipt for completed payments
    if (payment?.status === 'completed') {
      actions?.push({
        id: 'receipt',
        label: 'Download Receipt',
        icon: Download,
        onClick: handleDownloadReceipt,
        variant: 'outline',
        description: 'Get a PDF copy of your payment receipt'
      });
    }

    return actions;
  };

  const hasServices = () => {
    return order?.order_items?.some(item => item?.service_id);
  };

  const isServiceCompleted = () => {
    // Check if service date has passed
    if (order?.service_date) {
      return new Date(order.service_date) < new Date();
    }
    return false;
  };

  const handleDownloadReceipt = () => {
    // Create receipt content
    const receiptContent = `
CLEAN360 PAYMENT RECEIPT
========================

Payment ID: ${payment?.id}
Order ID: ${order?.id || 'N/A'}
Date: ${new Date(payment.created_at)?.toLocaleDateString('en-ZA')}

Customer: ${order?.customer_name || 'N/A'}
Email: ${order?.customer_email || 'N/A'}

Amount: R${payment?.amount?.toFixed(2)}
Currency: ${payment?.currency}
Payment Method: ${payment?.payment_gateway?.replace('_', ' ')?.toUpperCase()}
Status: ${payment?.status?.toUpperCase()}

${payment?.gateway_reference ? `Reference: ${payment?.gateway_reference}` : ''}
${payment?.gateway_transaction_id ? `Transaction ID: ${payment?.gateway_transaction_id}` : ''}

Items:
${order?.order_items?.map(item => 
  `- ${item?.services?.name || item?.products?.name} (Qty: ${item?.quantity}) - R${item?.total_price?.toFixed(2)}`
)?.join('\n') || 'No items listed'}

Thank you for choosing Clean360!
Support: support@clean360.co.za
Phone: +27 11 123 4567
    `;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clean360-receipt-${payment?.gateway_reference || payment?.id}.txt`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getVariantClasses = (variant) => {
    const variants = {
      primary: 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600',
      secondary: 'bg-green-600 text-white hover:bg-green-700 border-green-600',
      outline: 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
    };
    return variants?.[variant] || variants?.outline;
  };

  const actions = getAvailableActions();

  if (actions?.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Available Actions</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {actions?.map((action) => {
          const IconComponent = action?.icon;
          return (
            <div
              key={action?.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {action?.label}
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    {action?.description}
                  </p>
                  <button
                    onClick={action?.onClick}
                    className={`w-full px-3 py-2 border rounded-md text-sm font-medium transition-colors ${getVariantClasses(action?.variant)}`}
                  >
                    {action?.label}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Alternative Actions */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-4">Need More Help?</h4>
        <div className="flex flex-wrap gap-3">
          <button className="flex items-center px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            <MessageCircle className="h-4 w-4 mr-2" />
            Contact Support
          </button>
          <button className="flex items-center px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            Track Order Status
          </button>
          <button className="flex items-center px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">
            View Order History
          </button>
        </div>
      </div>
      {/* Payment Status Specific Messages */}
      {payment?.status === 'failed' && payment?.failure_reason && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <h4 className="text-sm font-medium text-red-900 mb-2">Payment Failed</h4>
          <p className="text-sm text-red-700">
            Reason: {payment?.failure_reason}
          </p>
          <p className="text-sm text-red-700 mt-1">
            Please try again or contact support if the issue persists.
          </p>
        </div>
      )}
      {payment?.status === 'refunded' && (
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-md">
          <h4 className="text-sm font-medium text-purple-900 mb-2">Payment Refunded</h4>
          <p className="text-sm text-purple-700">
            Your refund has been processed and should appear in your account within 3-5 business days.
            The exact timing depends on your bank or payment provider.
          </p>
        </div>
      )}
      {payment?.status === 'completed' && hasServices() && !order?.service_date && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <h4 className="text-sm font-medium text-yellow-900 mb-2">Schedule Your Service</h4>
          <p className="text-sm text-yellow-700">
            Your payment is complete! Don't forget to schedule your cleaning service appointment
            to get the most out of your purchase.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentActions;